import React from 'react';

import { Card, Col, Row, Statistic, Tooltip } from 'antd';

import { AgroReportSummary } from '../../../models/AgroReport';

interface Props {
    summary: AgroReportSummary;
}

// Человеческие названия для технических кодов из поля `missing`.
const MISSING_LABELS: Record<string, string> = {
    soil_moisture: 'влага в почве (нет станции в поле)',
    vegetation_start: 'начало вегетации',
    vegetation_end: 'конец вегетации',
    start_growing_t: 'порог начала вегетации',
    end_growing_t: 'порог конца вегетации',
};

const humanizeMissing = (code: string): string => MISSING_LABELS[code] || code;

export default function SummaryTiles(props: Props) {
    const s = props.summary;

    // Сумма продуктивной влаги в слое 0–60 см и её подсветка.
    // Пороги общие (для большинства зерновых/масличных):
    //   ≥120 мм — хорошо, 60–120 — средне, <60 — засушливо.
    let totalMM: number | undefined;
    if (
        s.productive_mm1 !== undefined &&
        s.productive_mm2 !== undefined &&
        s.productive_mm3 !== undefined
    ) {
        totalMM = s.productive_mm1 + s.productive_mm2 + s.productive_mm3;
    }

    const noSoilData = s.missing.includes('soil_moisture');

    let totalStyle: React.CSSProperties | undefined;
    if (!noSoilData && totalMM !== undefined) {
        if (totalMM >= 120) {
            totalStyle = { background: '#f6ffed', borderColor: '#b7eb8f' };
        } else if (totalMM >= 60) {
            totalStyle = { background: '#fffbe6', borderColor: '#ffe58f' };
        } else {
            totalStyle = { background: '#fff1f0', borderColor: '#ffa39e' };
        }
    }

    // «Полезные» missing — те, что стоит показать пользователю.
    // soil_moisture показываем отдельной плашкой, если станции нет.
    const otherMissing = s.missing.filter((m) => m !== 'soil_moisture');

    return (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
                <Card>
                    <Tooltip title="Ожидаемая урожайность культуры при текущих условиях">
                        <Statistic
                            title="Урожайность, ц/га"
                            value={s.yield !== undefined ? s.yield : '—'}
                            precision={s.yield !== undefined ? 1 : undefined}
                            suffix={
                                s.yield_category ? (
                                    <span style={{ fontSize: 12, color: '#888' }}>
                                        {s.yield_category}
                                    </span>
                                ) : null
                            }
                        />
                    </Tooltip>
                </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
                <Card>
                    <Tooltip title="Сумма активных температур выше 10°C за период вегетации">
                        <Statistic
                            title="САТ выше 10°C"
                            value={s.sat10 !== undefined ? s.sat10 : '—'}
                            precision={0}
                            suffix="°C·дн"
                        />
                    </Tooltip>
                </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
                <Card>
                    <Tooltip title="Гидротермический коэффициент Селянинова. Оптимально для большинства культур: 1.0–1.5.">
                        <Statistic
                            title="ГТК"
                            value={s.gtk !== undefined ? s.gtk : '—'}
                            precision={2}
                            suffix={s.gtk_category ? (
                                <span style={{ fontSize: 12, color: '#888' }}>
                                    {s.gtk_category}
                                </span>
                            ) : null}
                        />
                    </Tooltip>
                </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
                <Card>
                    <Statistic
                        title="Текущая фаза"
                        value={s.current_phase || '—'}
                    />
                </Card>
            </Col>
            <Col xs={24}>
                <Card style={totalStyle}>
                    <Tooltip title="Суммарная продуктивная влага в слое 0–60 см. Оптимально для большинства культур: 120–180 мм. При меньших значениях — риск засухи.">
                        <Statistic
                            title="Итого продуктивная влага 0–60 см"
                            value={
                                noSoilData
                                    ? 'нет данных'
                                    : totalMM !== undefined
                                    ? totalMM
                                    : '—'
                            }
                            precision={!noSoilData && totalMM !== undefined ? 1 : undefined}
                            suffix={!noSoilData && totalMM !== undefined ? 'мм' : undefined}
                        />
                    </Tooltip>
                </Card>
            </Col>
            {noSoilData && (
                <Col xs={24}>
                    <Card style={{ background: '#f0f5ff', borderColor: '#adc6ff' }}>
                        <span style={{ color: '#1d39c4' }}>
                            В поле нет метеостанции — данных о влаге в почве нет.
                            Показаны только погодные данные (Open-Meteo):
                            температура воздуха, осадки, САТ, ГТК, фазы, рекомендации.
                        </span>
                    </Card>
                </Col>
            )}
            {s.data_incomplete && otherMissing.length > 0 && (
                <Col xs={24}>
                    <Card style={{ background: '#fff7e6', borderColor: '#ffd591' }}>
                        <span style={{ color: '#d46b08' }}>
                            Информация о культуре уточняется. Не рассчитано:{' '}
                            {otherMissing.map(humanizeMissing).join(', ')}
                        </span>
                    </Card>
                </Col>
            )}
        </Row>
    );
}
